"use client"
import React, { useState, useCallback, useEffect } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { ChevronRight, Folder } from "lucide-react"
import type { Name_lesson } from "@/lib/types/course/detail"
import { cn } from "@/lib/utils"
import { formatDuration } from "@/lib/utils/formatter"
import { Badge } from "@/components/ui/badge"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Button } from "@/components/ui/button"
import { LessonItem } from "./lesson/LessonItem"

interface Chapter {
	name: string
	id: number
	lessons: Name_lesson[]
}

const DirectoryTree = ({
	lessons,
	slug,
	courseDetail,
}: {
	lessons: Name_lesson[]
	slug: string
	courseDetail: any
}) => {
	const [expandedChapters, setExpandedChapters] = useState<number[]>([])
	const [hoveredLesson, setHoveredLesson] = useState<number | null>(null)
	const [lastLesson, setLastLesson] = useState<number | null>(null)

	const rawData = lessons

	// Process data to group lessons by chapters
	const processData = (): Chapter[] => {
		const chapters: Chapter[] = []
		const lessonsByChapter = new Map<number, Name_lesson[]>()

		for (const item of rawData) {
			if (item.depend_id === 0) {
				chapters.push({
					name: item.name,
					id: Number(item.id),
					lessons: [],
				})
			} else {
				if (!lessonsByChapter.has(item.depend_id)) {
					lessonsByChapter.set(item.depend_id, [])
				}
				lessonsByChapter.get(item.depend_id)?.push(item)
			}
		}

		chapters.sort((a, b) => a.id - b.id)
		for (const chapter of chapters) {
			const chapterLessons = lessonsByChapter.get(chapter.id) || []
			chapter.lessons = chapterLessons.sort(
				(a, b) => (a.stt ?? 0) - (b.stt ?? 0),
			)
		}

		return chapters
	}

	const findLastLesson = useCallback(() => {
		let lastChapterId = -1
		let lastLessonId = -1
		let lastLearnedLesson = null

		// First sort rawData by stt and id
		const sortedLessons = [...rawData].sort((a, b) => {
			if (a.stt !== b.stt) {
				return (a.stt ?? 0) - (b.stt ?? 0)
			}
			return Number(a.id) - Number(b.id)
		})

		// Find the last unlearned lesson
		for (const item of sortedLessons) {
			if (item.depend_id !== 0 && !item.learned) {
				lastLessonId = Number(item.id)
				lastChapterId = item.depend_id
				lastLearnedLesson = item
				break
			}
		}

		if (lastChapterId !== -1) {
			setLastLesson(lastLessonId)
			setExpandedChapters([lastChapterId])
		} else {
			// Find the first lesson by stt
			const firstLesson = sortedLessons.find(lesson => lesson.depend_id !== 0)
			if (firstLesson) {
				setLastLesson(Number(firstLesson.id))
				setExpandedChapters([firstLesson.depend_id])
			}
		}
	}, [rawData])

	useEffect(() => {
		findLastLesson()
	}, [findLastLesson])

	const toggleChapter = (chapterId: number) => {
		setExpandedChapters((prev) =>
			prev.includes(chapterId)
				? prev.filter((id) => id !== chapterId)
				: [chapterId],
		)
	}

	const chapters = processData()

	return (
		<ScrollArea className="min-h-[500px]">
			<div className="mx-auto bg-white p-1 md:p-4">
				<div className="space-y-1">
					{chapters.map((chapter) => (
						<div key={chapter.id} className="select-none">
							<Button
								variant="ghost"
								className={cn(
									"flex w-full cursor-pointer items-center rounded-md p-1 transition-colors md:p-2.5",
									"whitespace-normal text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2",
									"min-h-fit"
								)}
								onClick={() => toggleChapter(chapter.id)}
								aria-expanded={expandedChapters.includes(chapter.id)}
							>
								<motion.div
									initial={false}
									animate={{
										rotate: expandedChapters.includes(chapter.id) ? 90 : 0,
									}}
									transition={{ duration: 0.2 }}
									className="mr-1 shrink-0 md:mr-2"
								>
									<ChevronRight className="h-4 w-4 text-gray-500" />
								</motion.div>

								<div className="flex w-full items-center justify-between">
									<div className="flex items-center">
										<motion.div
											whileHover={{ scale: 1.05 }}
											transition={{ duration: 0.2 }}
											className="shrink-0"
										>
											<Folder className="mr-1 h-5 w-5 text-yellow-500 md:mr-2" />
										</motion.div>
										<span className="break-words font-medium text-gray-700">
											{chapter.name}
										</span>
									</div>
									<Badge variant="secondary" className="ml-1 shrink-0 md:ml-2">
										{chapter.lessons.length} <span className="hidden text-xs md:ml-1 md:inline">bài học</span>
									</Badge>
								</div>
							</Button>

							<AnimatePresence>
								{expandedChapters.includes(chapter.id) && (
									<motion.div
										initial={{ height: 0, opacity: 0 }}
										animate={{ height: "auto", opacity: 1 }}
										exit={{ height: 0, opacity: 0 }}
										transition={{ 
											duration: 0.3,
											ease: [0.4, 0, 0.2, 1]
										}}
										className="overflow-hidden"
									>
										<div className="ml-1 space-y-1 border-gray-200 border-l py-1 pl-2 md:ml-6 md:pl-4">
											{chapter.lessons.map((lesson) => (
												<LessonItem
													key={lesson.id}
													lesson={lesson}
													slug={slug}
													isHovered={hoveredLesson === Number(lesson.id)}
													onMouseEnter={() =>
														setHoveredLesson(Number(lesson.id))
													}
													onMouseLeave={() => setHoveredLesson(null)}
													formatDuration={formatDuration}
													isLastLesson={lastLesson === Number(lesson.id)}
													courseDetail={courseDetail}
												/>
											))}
										</div>
									</motion.div>
								)}
							</AnimatePresence>
						</div>
					))}
				</div>
			</div>
		</ScrollArea>
	)
}

export default DirectoryTree
